export const formatPhoneNumber = (value: string) => {
    let sanitizedValue = value.replace(/\D/g, '');

    sanitizedValue = sanitizedValue.slice(0, 10);

    if (sanitizedValue.length === 10) {
        return sanitizedValue.replace(/(\d{4})(\d{3})(\d{3})/, '$1-$2-$3');
    }

    return sanitizedValue;
};

/** Formats phone as user types: 0000-000-000 (hyphen after 4th and 7th digit) */
export const formatPhoneNumberAsYouType = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 4) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
    return `${digits.slice(0, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
};

export function convertToEnglishDate(inputDate: string): string {
    if (!inputDate) {
        return "";
    }
    const ENGLISH_MONTHS = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const [year, month, day] = inputDate.split('-').map(Number);
    if (!year || !month || !day) return "";

    const monthName = ENGLISH_MONTHS[month - 1];
    if (!monthName) return "";

    return `${monthName} ${day}, ${year}`;
}

export function convertToShortEnDate(inputDate: string): string {
    if (!inputDate) {
        return "";
    }
    const ENGLISH_MONTHS = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const [year, month, day] = inputDate.split('-').map(Number);

    if (!year || !month || !day) return "";

    const monthName = ENGLISH_MONTHS[month - 1];
    if (!monthName) return "";

    return `${monthName} ${day}, ${year}`;
}

export function getMonthNumberFromTWMonth(enMonth: string | string[]): number | (number | null)[] | null {
    const twMonths: string[] = [
        "一月", "二月", "三月", "四月",
        "五月", "六月", "七月", "八月",
        "九月", "十月", "十一月", "十二月"
    ];

    if (typeof enMonth === 'string') {
        const monthIndex = twMonths.indexOf(enMonth);
        return monthIndex !== -1 ? monthIndex + 1 : null;
    } else if (Array.isArray(enMonth) && enMonth.every(item => typeof item === 'string')) {
        return enMonth.map(month => {
            const monthIndex = twMonths.indexOf(month);
            return monthIndex !== -1 ? monthIndex + 1 : null;
        });
    } else {
        return null;
    }
}

export function getTWMonthFromNumber(monthNumber: number | number[]): string | (string | null)[] | null {

    const twMonths: string[] = [
        "一月", "二月", "三月", "四月",
        "五月", "六月", "七月", "八月",
        "九月", "十月", "十一月", "十二月"
    ];

    if (typeof monthNumber === 'number') {
        if (monthNumber >= 1 && monthNumber <= 12) {
            return twMonths[monthNumber - 1];
        } else {
            return null;
        }
    } else if (Array.isArray(monthNumber)) {
        return monthNumber.map(month => {
            if (typeof month === 'number' && month >= 1 && month <= 12) {
                return twMonths[month - 1];
            } else {
                return null;
            }
        });
    } else {
        return null;
    }
}

export function formatWithTWExtension(input: string) {
    if (!input) {
        return '+88';
    }

    const digitsOnly = input.replace(/\D/g, '');

    if (digitsOnly.startsWith('0')) {
        const withoutLeadingZero = digitsOnly.substring(1);
        return '+88' + withoutLeadingZero;
    } else {
        return '+88' + digitsOnly;
    }
}

export function extractURLBeforeContext(url: string) {
    let contextIndex = url.indexOf('#context');
    if (contextIndex !== -1) {
        return url.substring(0, contextIndex);
    }

    let accessIndex = url.indexOf('#access');
    if (accessIndex !== -1) {
        return url.substring(0, accessIndex);
    }
    return url;
}

const taipeiGregory: Intl.DateTimeFormatOptions = {
    timeZone: 'Asia/Taipei',
    calendar: 'gregory',
};

const pickPart = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPart['type']) =>
    parts.find((p) => p.type === type)?.value;

export type TaiwanLogDateTime = {
    formattedDate: string;
    timestamp: string;
    timeSlot: string;
};

export function getTaiwanLogDateTime(now: Date = new Date()): TaiwanLogDateTime {
    const parts = new Intl.DateTimeFormat('zh-TW', {
        ...taipeiGregory,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        hourCycle: 'h23',
    }).formatToParts(now);

    const y = pickPart(parts, 'year');
    const mo = pickPart(parts, 'month');
    const d = pickPart(parts, 'day');
    const hh = pickPart(parts, 'hour');
    const mm = pickPart(parts, 'minute');
    const ss = pickPart(parts, 'second');

    const formattedDate =
        y != null && mo != null && d != null
            ? `${y}-${mo}-${d}`
            : new Intl.DateTimeFormat('en-CA', {
                ...taipeiGregory,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }).format(now);

    const timestamp =
        y != null && mo != null && d != null && hh != null && mm != null && ss != null
            ? `${y}-${mo}-${d} ${hh}:${mm}:${ss} (Asia/Taipei)`
            : new Intl.DateTimeFormat('zh-TW', {
                ...taipeiGregory,
                dateStyle: 'medium',
                timeStyle: 'medium',
                hour12: false,
            }).format(now);

    let hour = Number.parseInt(hh ?? '', 10);
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
        hour = (now.getUTCHours() + 8) % 24;
    }
    hour = Math.max(0, Math.min(23, hour));

    const start = Math.min(20, Math.floor(hour / 4) * 4);
    const end = start + 4;
    const timeSlot = `${String(start).padStart(2, '0')}-${end === 24 ? '00' : String(end).padStart(2, '0')}`;

    return { formattedDate, timestamp, timeSlot };
}